import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";

import { apiService } from "../../../services/api.service";

import { Modal, useModal } from "../../../components/Modal/Modal";
import ContextMenu, { IContextMenuOption } from "../../../components/ContextMenu/ContextMenu";
import { toast } from "../../../components/Toast/ToastManager";

import { type ColDef, GridApi, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { ActionsRenderer } from "./ActionsRenderer";
import { StatusRenderer } from "./StatusRenderer";
import GenerateBulkCodesModal from "./GenerateBulkCodesModal";

import styles from "./BulkDiscountCodesTab.module.scss";

interface IProps {
  currentTab: number;
  tabIndex: number;
}

interface IBulkDiscountCodeRow {
  _id: string;
  code: string;
  batch_id: string;
  batch_name: string;
  is_used: boolean;
  used_at?: string;
  used_by?: string;
  transaction_id?: string;
  expires_at?: string;
  created_at: string;
  status: string;
  actions?: string; // For AGGrid actions column
}

const BulkDiscountCodesTab: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [rowData, setRowData] = useState<IBulkDiscountCodeRow[]>([]);
  // const [analytics, setAnalytics] = useState<any>();
  const [gridApi, setGridApi] = useState<GridApi>();
  
  const { isShown, toggle } = useModal();
  const [, setModalContent] = useState<string>("");

  const theme = themeQuartz;

  const getBulkDiscountCodes = useCallback(async () => {
    try {
      const codes = await apiService.getAllBulkDiscountCodes();
      
      const formattedData: IBulkDiscountCodeRow[] = codes.map(code => {
        // Determine status
        let status = 'active';
        if (code.is_used) {
          status = 'used';
        } else if (code.expires_at && new Date(code.expires_at) < new Date()) {
          status = 'expired';
        }

        return {
          _id: code._id,
          code: code.code,
          batch_id: code.batch_id,
          batch_name: code.batch_name,
          is_used: code.is_used,
          used_at: code.used_at ? code.used_at.toString() : undefined,
          used_by: code.used_by,
          transaction_id: code.transaction_id,
          expires_at: code.expires_at ? code.expires_at.toString() : undefined,
          created_at: code.created_at ? code.created_at.toString() : new Date().toISOString(),
          status,
          actions: undefined
        };
      });
      
      setRowData(formattedData);
    } catch (error) {
      console.error('Error loading bulk discount codes:', error);
      toast.show({
        title: "Error",
        content: "Failed to load bulk discount codes",
        duration: 5000,
        type: "fail"
      });
    }
  }, []);

  const getAnalytics = useCallback(async () => {
    try {
      // const analytics = await apiService.getBulkDiscountAnalytics();
      // setAnalytics(analytics);
    } catch (error) {
      console.error("Failed to load analytics:", error);
      toast.show({
        title: "Error",
        content: "Failed to load analytics",
        duration: 5000,
        type: "fail"
      });
    }
  }, []);

  // Generate CSV content from data
  const generateCSV = useCallback((data: IBulkDiscountCodeRow[]) => {
    if (!data || data.length === 0) {
      return '';
    }

    // Define the columns we want to export (same as the builder)
    const columns = [
      { key: 'code', header: 'Code' },
      { key: 'batch_name', header: 'Batch Name' },
      { key: 'status', header: 'Status' },
      { key: 'is_used', header: 'Is Used' },
      { key: 'used_at', header: 'Used At' },
      { key: 'used_by', header: 'Used By' },
      { key: 'transaction_id', header: 'Transaction ID' },
      { key: 'expires_at', header: 'Expires At' },
      { key: 'created_at', header: 'Created At' },
      { key: 'created_by', header: 'Created By' },
      { key: 'batch_id', header: 'Batch ID' }
    ];

    // Create header row
    const headers = columns.map(col => col.header).join(',');
    
    // Create data rows
    const rows = data.map(row => {
      return columns.map(col => {
        const rawValue = (row as any)[col.key];
        const value = rawValue != null ? String(rawValue) : '';
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',');
    });

    return [headers, ...rows].join('\n');
  }, []);

  // Download CSV file
  const downloadCSV = useCallback((csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }, []);

  const handleExport = useCallback(async () => {
    try {
      // Show loading toast
      toast.show({
        title: "Export",
        content: "Generating CSV file with filtered results...",
        duration: 3000,
        type: "fail"
      });

      // Get currently displayed (filtered) data from AG Grid
      let dataToExport: IBulkDiscountCodeRow[] = [];
      
      if (gridApi) {
        // Get all displayed row data (respects current filters and sorting)
        gridApi.forEachNodeAfterFilterAndSort((node: any) => {
          if (node.data) {
            dataToExport.push(node.data);
          }
        });
      } else {
        // Fallback to all data if grid API not available
        dataToExport = rowData;
      }

      // Convert to CSV and download directly
      const csvContent = generateCSV(dataToExport);
      downloadCSV(csvContent, `bulk-discount-codes-${new Date().toISOString().split('T')[0]}.csv`);

      toast.show({
        title: "Export Successful",
        content: "Bulk discount codes exported successfully",
        duration: 3000,
        type: "success"
      });
    } catch (error) {
      console.error("Export failed:", error);
      toast.show({
        title: "Export Failed",
        content: "Failed to export bulk discount codes",
        duration: 5000,
        type: "fail"
      });
    }
  }, [gridApi, rowData, downloadCSV, generateCSV]);

  const menuActions: IContextMenuOption[] = [
    {
      label: "Generate New Batch",
      onClick: () => {
        setModalContent("generate");
        toggle();
      }
    },
    {
      label: "Export Filtered Codes",
      onClick: () => {
        handleExport();
      }
    },
    {
      label: "Refresh Data",
      onClick: () => {
        getBulkDiscountCodes();
        getAnalytics();
      }
    }
  ];

  const colDefs: ColDef<IBulkDiscountCodeRow>[] = [
    { 
      headerName: "Code", 
      field: "code", 
      minWidth: 150, 
      filter: true,
      pinned: "left"
    },
    { 
      headerName: "Batch", 
      field: "batch_name", 
      minWidth: 200, 
      filter: true 
    },
    {
      headerName: "Status",
      field: "status",
      minWidth: 120,
      filter: true,
      cellRenderer: StatusRenderer,
    },
    { 
      headerName: "Used At", 
      field: "used_at", 
      minWidth: 150, 
      filter: true,
      valueFormatter: (params) => {
        return params.value ? moment(params.value).format("MMM DD, YYYY") : "-";
      }
    },
    { 
      headerName: "Expires", 
      field: "expires_at", 
      minWidth: 150, 
      filter: true,
      valueFormatter: (params) => {
        return params.value ? moment(params.value).format("MMM DD, YYYY") : "Never";
      }
    },
    { 
      headerName: "Created", 
      field: "created_at", 
      minWidth: 150, 
      filter: true,
      valueFormatter: (params) => {
        return moment(params.value).format("MMM DD, YYYY");
      }
    },
    {
      headerName: "",
      field: "actions",
      maxWidth: 50,
      minWidth: 50,
      cellRenderer: ActionsRenderer,
      pinned: "right",
      cellRendererParams: {
        refreshData: () => {
          getBulkDiscountCodes();
          getAnalytics();
        }
      },
      resizable: false,
      cellStyle: {
        "overflow": "visible",
        "zIndex": "2"
      }
    }
  ];



  const onGridReady = (params: any) => {
    setGridApi(params.api);
  };

  const handleGenerateSuccess = () => {
    getBulkDiscountCodes();
    getAnalytics();
    toggle();
  };



  useEffect(() => {
    if (props.currentTab === props.tabIndex) {
      getBulkDiscountCodes();
      getAnalytics();
    }
  }, [props.currentTab, props.tabIndex, getBulkDiscountCodes, getAnalytics]);

  useEffect(() => {
    if (!loaded) {
      getBulkDiscountCodes();
      getAnalytics();
      setLoaded(true);
    }
  }, [loaded, getBulkDiscountCodes, getAnalytics]);

  return (
    <div className={styles.BulkDiscountCodesTab}>
      <div className={styles.actions}>
        <div className={styles.left}>
          {/* Future filter controls can go here */}
        </div>
        <div className={styles.right}>
          <span className={styles.actionsLabel}>Actions</span>
          <ContextMenu options={menuActions} />
        </div>
      </div>

      <div className={styles.tableWrap}>
        <div className={`ag-theme-quartz ${styles.grid}`}>
          <AgGridReact
            theme={theme}
            rowData={rowData}
            columnDefs={colDefs}
            onGridReady={onGridReady}
            pagination={true}
            paginationPageSize={50}
            defaultColDef={{
              resizable: true,
              sortable: true,
              filter: true
            }}
            overlayNoRowsTemplate="<span style='padding: 20px; font-size: 16px; color: #666;'>No Bulk Discount Codes have been created yet</span>"
          />
        </div>
      </div>

                    <Modal
        isShown={isShown}
        hide={toggle}
        className="generateBulkCodesModal"
        modalContent={
          <GenerateBulkCodesModal
            onCancel={toggle}
            onSuccess={handleGenerateSuccess}
          />
        }
      />
    </div>
  );
};

export default BulkDiscountCodesTab; 